import { css } from '@emotion/react';
import { Typography } from '@mui/material';
import { colors } from '../../../../../theme/colors';
import { GRID_COLS, GRID_MAX_ROWS, GRID_ROW_HEIGHT, GRID_WIDTH, TEARSHEET_PANELS } from '../constants';
import { TearsheetPanelType } from '../types';
import { GridRect } from '../utils/gridOccupancy';

const CELL_WIDTH = GRID_WIDTH / GRID_COLS;
const CELL_HEIGHT = GRID_ROW_HEIGHT;

const previewBaseStyle = css`
  position: absolute;
  border-radius: 4px;
  pointer-events: none;
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  box-sizing: border-box;
  transition:
    transform 50ms ease-out,
    width 50ms ease-out,
    height 50ms ease-out;
`;

const validPreviewStyle = css`
  background: ${colors.neutral[5]};
  border: 1px solid ${colors.neutral[30]};
`;

const invalidPreviewStyle = css`
  background: rgba(239, 68, 68, 0.1);
  border: 2px dashed rgba(239, 68, 68, 0.5);
`;

interface DragPreviewProps {
  rect: GridRect | null;
  panelType: TearsheetPanelType | null;
  isDragging: boolean;
  cursorGridPos?: { x: number; y: number } | null;
  itemSize?: { w: number; h: number };
}

export function DragPreview({ rect, panelType, isDragging, cursorGridPos, itemSize }: DragPreviewProps) {
  if (!isDragging || !panelType) {
    return null;
  }

  // If we have a valid rect, show it
  if (rect) {
    const style: React.CSSProperties = {
      transform: `translate(${rect.x * CELL_WIDTH}px, ${rect.y * CELL_HEIGHT}px)`,
      width: rect.w * CELL_WIDTH,
      height: rect.h * CELL_HEIGHT,
    };

    return (
      <div css={[previewBaseStyle, validPreviewStyle]} style={style}>
        <Typography variant='body2' fontWeight={500}>
          {TEARSHEET_PANELS[panelType].label}
        </Typography>
      </div>
    );
  }

  // No valid rect - show invalid state at cursor position if available
  if (cursorGridPos && itemSize) {
    // Clamp position to grid bounds for display
    const x = Math.max(0, Math.min(cursorGridPos.x, GRID_COLS - 1));
    const y = Math.max(0, Math.min(cursorGridPos.y, GRID_MAX_ROWS - 1));

    const style: React.CSSProperties = {
      transform: `translate(${x * CELL_WIDTH}px, ${y * CELL_HEIGHT}px)`,
      width: Math.min(itemSize.w, GRID_COLS - x) * CELL_WIDTH,
      height: Math.min(itemSize.h, GRID_MAX_ROWS - y) * CELL_HEIGHT,
    };

    return (
      <div css={[previewBaseStyle, invalidPreviewStyle]} style={style}>
        <Typography variant='body2' fontWeight={500} color='error'>
          {TEARSHEET_PANELS[panelType].label}
        </Typography>
      </div>
    );
  }

  return null;
}
